/**
 * useStatementUploader — single source of truth for POSTing a bank
 * statement file to `/api/bank-feeds/upload-background`.
 *
 * Why this exists
 * ---------------
 * Two entry points used to duplicate the multipart upload + backgroundJobs
 * registration + error-shape handling:
 *   1. `pages/MyBanksPage::handleStatementUpload` (upload from a bank
 *      account row on the Bank Accounts page)
 *   2. `pages/BankFeedCenterPage::executeUpload` (upload from the Bank
 *      Feed Center with the confirm-account dialog)
 * They drifted: MyBanks had a dead `data.billing_preview` branch;
 * BankFeeds had the validation-mismatch UI and local page-polling.
 * Any future upload endpoint change (idempotency key, chunking, retry
 * policy) had to be applied in both places.
 *
 * This hook owns:
 *   - the multipart POST with correct headers + progress callback
 *   - registration into `BackgroundJobsContext` so ANY page shows the banner
 *   - translating server error shapes into typed errors the caller can
 *     handle with page-specific UX (validation warning, toast, redirect)
 *
 * The hook does NOT own:
 *   - page-level state (which dialog is open, which file was picked)
 *   - page-level polling for the on-page live banner
 *   - navigation
 *
 * Callers keep those, which is why this is a hook (not a full component)
 * and why the API is a plain async function returning `{ jobId, filename }`.
 */
import axios from 'axios';
import { useCallback } from 'react';

import { useBackgroundJobs } from '../contexts/BackgroundJobsContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Thrown when the server rejects the upload because the file's period/name
 * does not match the chosen bank account. Callers show either a warning
 * dialog (BankFeedCenter) or a toast (MyBanks).
 */
export class StatementValidationMismatchError extends Error {
  constructor(reason, rawDetail) {
    super(reason || 'Statement validation failed');
    this.name = 'StatementValidationMismatchError';
    this.mismatchReason = reason;
    this.rawDetail = rawDetail;
  }
}

/**
 * Thrown when the axios interceptor blocked the 401-auto-replay of the
 * multipart POST (see App.js NON_REPLAYABLE_UPLOAD guard). Caller must
 * show a friendly "please click Upload again" toast — the token is being
 * refreshed in the background so the next click will succeed.
 */
export class StatementAuthRefreshError extends Error {
  constructor() {
    super('Session refreshed - please retry the upload');
    this.name = 'StatementAuthRefreshError';
  }
}

/**
 * @typedef {Object} UploadOptions
 * @property {string} bankAccountId       - required
 * @property {boolean} [skipValidation]   - forwarded to the endpoint
 * @property {(pct:number)=>void} [onProgress] - upload-percent callback
 * @property {boolean} [addToGlobalTracker=true] - register with BackgroundJobsContext
 */

/**
 * @returns {{
 *   uploadStatement: (file: File, opts: UploadOptions) => Promise<{jobId:string, filename:string, response:any}>
 * }}
 */
export function useStatementUploader() {
  const backgroundJobsCtx = useBackgroundJobs();

  const uploadStatement = useCallback(async (file, opts) => {
    if (!file) throw new Error('file is required');
    if (!opts || !opts.bankAccountId) throw new Error('bankAccountId is required');

    const fd = new FormData();
    fd.append('file', file);
    fd.append('bank_account_id', opts.bankAccountId);
    if (opts.skipValidation) fd.append('skip_validation', 'true');

    // iter1443 R8 · Idempotency key. If the network / Cloudflare drops
    // the response mid-flight (the CF-520 Magma Seven Fitness hit), the
    // caller can retry with the SAME key and the server will return the
    // original job_id instead of creating a duplicate upload.
    // crypto.randomUUID is available in every browser we ship to.
    const idempotencyKey = (typeof crypto !== 'undefined' && crypto.randomUUID)
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

    let response;
    try {
      response = await axios.post(`${API}/bank-feeds/upload-background`, fd, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Idempotency-Key': idempotencyKey,
        },
        onUploadProgress: (e) => {
          if (typeof opts.onProgress === 'function' && e.total) {
            opts.onProgress(Math.round((e.loaded * 100) / e.total));
          }
        },
      });
    } catch (err) {
      // 401 replay-blocked (see App.js NON_REPLAYABLE_UPLOAD). Do NOT
      // re-fire the multipart POST - that's what caused the duplicate-jobs
      // bug. Throw a typed error so the caller can prompt the user.
      if (err?.__uploadRetryBlocked || err?.response?.status === 401) {
        throw new StatementAuthRefreshError();
      }
      // Server-side validation mismatch (e.g., statement period doesn't
      // match the account currency or bank name). Surface a typed error
      // with the human-readable reason so the caller can show either an
      // in-dialog warning (BankFeedCenter) or a toast (MyBanks).
      const detail = err?.response?.data?.detail || '';
      if (
        typeof detail === 'string'
        && (detail.includes('validation failed') || detail.includes('mismatch'))
      ) {
        const reason = detail
          .replace('Statement validation failed: ', '')
          .split('. If you')[0];
        throw new StatementValidationMismatchError(reason, detail);
      }
      // iter1443 R8 · Normalize `err.response.data.detail` so every
      // caller can safely `toast.error(err.userMessage)` without ever
      // rendering "[object Object]". Backend returns structured
      // { error_code, message } for the new 504 gridfs_store_timeout
      // and 502 gridfs_store_failed branches; older endpoints still
      // return plain strings. Coerce both shapes to a single string.
      let userMessage = 'Failed to upload file. Please try again.';
      if (typeof detail === 'string' && detail.trim()) {
        userMessage = detail.trim();
      } else if (detail && typeof detail === 'object') {
        userMessage = detail.message
          || detail.detail
          || detail.error
          || userMessage;
      } else if (err?.message && typeof err.message === 'string') {
        // Axios network error (no response) — e.g. Cloudflare 520
        // aborts before FastAPI runs. Surface a friendly retry hint.
        if (err.code === 'ERR_NETWORK' || !err.response) {
          userMessage = 'We could not reach the server. Check your internet and click Process Statement again — your last attempt will be skipped as a duplicate so nothing gets processed twice.';
        }
      }
      err.userMessage = userMessage;
      throw err;
    }

    const jobId = response.data.job_id;
    const filename = response.data.filename;

    // Global tracker registration - so the banner appears on ANY page
    // (Bank Feeds, MyBanks, Dashboard, or a hard-refreshed tab). Callers
    // that need the on-page live progress still add to their own local
    // state; this just ensures the CROSS-page banner works.
    const addToGlobal = opts.addToGlobalTracker !== false;
    if (addToGlobal && backgroundJobsCtx?.addJob) {
      try { backgroundJobsCtx.addJob({ jobId, filename }); } catch (_) { /* non-fatal */ }
    }

    return { jobId, filename, response };
  }, [backgroundJobsCtx]);

  // Statement set · several files for ONE account in one request. The server parses them in parallel, joins them
  // with the continuity engine (balance parity at every join) and runs the normal pipeline once on the merged set.
  const uploadStatementSet = useCallback(async (files, opts) => {
    const list = Array.from(files || []).filter(Boolean);
    if (!list.length) throw new Error('files are required');
    if (list.length === 1) return uploadStatement(list[0], opts);
    if (!opts || !opts.bankAccountId) throw new Error('bankAccountId is required');
    const fd = new FormData();
    list.forEach((f) => fd.append('files', f));
    fd.append('bank_account_id', opts.bankAccountId);
    let response;
    try {
      response = await axios.post(`${API}/bank-feeds/upload-batch`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => { if (typeof opts.onProgress === 'function' && e.total) opts.onProgress(Math.round((e.loaded * 100) / e.total)); },
      });
    } catch (err) {
      if (err?.__uploadRetryBlocked || err?.response?.status === 401) throw new StatementAuthRefreshError();
      const detail = err?.response?.data?.detail;
      err.userMessage = (typeof detail === 'string' && detail) || detail?.message || 'Failed to upload the statements. Please try again.';
      throw err;
    }
    const jobId = response.data.job_id;
    const filename = `Statement set (${list.length} files)`;
    if (opts.addToGlobalTracker !== false && backgroundJobsCtx?.addJob) {
      try { backgroundJobsCtx.addJob({ jobId, filename }); } catch (_) { /* non-fatal */ }
    }
    return { jobId, filename, response, count: list.length };
  }, [backgroundJobsCtx, uploadStatement]);

  return { uploadStatement, uploadStatementSet };
}

export default useStatementUploader;
